import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SurveyService } from "../api/SurveyService";
import { WorkplaceCultureService } from "../api/WorkplaceCultureService";
import { InsightsCard } from "../components/InsightsCard/InsightsCard";
import { QuestionBreakdownOverview } from "../components/QuestionBreakdownOverview/QuestionBreakdownOverview";
import { WorkplaceCultureOverview } from "../components/WorkplaceCultureOverview/WorkplaceCultureOverview";
import { LoadingIndicator } from "../components/ui/LoadingIndicator/LoadingIndicator";
import { GetSurveyDispatchResponse } from "../types/survey";
import {
  GetQuestionBreakdownResponse,
  GetSurveyChapterInsightResponse,
  GetSurveyChapterScoringResponse,
  QuestionBreakdown as IQuestionBreakdown,
  SurveyChapterInsight,
  SurveyChapterScoring,
} from "../types/workplaceCulture";
import {
  OVERVIEW_DEMOGRAPHIC_GROUP_ID,
  isSurveyClosed,
  surveyHasAnswers,
} from "../utils/WorkplaceCulture.utils";

export const WorkplaceCultureChapterPage = () => {
  const surveyService = new SurveyService();
  const workplaceCultureService = new WorkplaceCultureService();
  const [chapterScoring, setChapterScoring] = useState<SurveyChapterScoring>();
  const [questionBreakdownForChapter, setQuestionBreakdownForChapter] =
    useState<IQuestionBreakdown[]>([]);
  const [selectedDemographicFilter, setSelectedDemographicFilter] =
    useState<string>(OVERVIEW_DEMOGRAPHIC_GROUP_ID);
  const [chapterInsights, setChapterInsights] = useState<
    SurveyChapterInsight[]
  >([]);

  const { dispatchId, chapterId } = useParams();
  const navigate = useNavigate();

  const {
    data: surveyDispatchData,
    isLoading: surveyDispatchLoading,
    error: surveyDispatchError,
  } = useQuery<GetSurveyDispatchResponse, AxiosError>(
    [`surveyDispatch-${dispatchId}`],
    () => surveyService.getDispatch(dispatchId as string)
  );

  const {
    data: surveyChapterScoringData,
    isLoading: surveyChapterScoringLoading,
    error: surveyChapterScoringError,
    refetch: surveyChapterScoringRefetch,
  } = useQuery<GetSurveyChapterScoringResponse, AxiosError>(
    [
      `surveyChapterScoring-${surveyDispatchData?.id}`,
      { filterId: selectedDemographicFilter },
    ],
    () =>
      workplaceCultureService.getSurveyChapterScoring(
        surveyDispatchData?.id || "",
        selectedDemographicFilter
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  const {
    data: surveyQuestionsScoring,
    isLoading: surveyQuestionsScoringLoading,
    error: surveyQuestionsScoringError,
  } = useQuery<GetQuestionBreakdownResponse, AxiosError>(
    [`surveyQuestionsChapterScoring-${surveyDispatchData?.id}`],
    () =>
      workplaceCultureService.getSurveyQuestionsScoring(
        surveyDispatchData?.id || ""
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  const {
    data: surveyChapterInsightsData,
    isLoading: chapterInsightsLoading,
    error: chapterInsightsError,
  } = useQuery<GetSurveyChapterInsightResponse, AxiosError>(
    [`surveyChapterInsights-${surveyDispatchData?.id}`],
    () =>
      workplaceCultureService.getSurveyChapterInsights(
        surveyDispatchData?.id || ""
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  useEffect(() => {
    if (surveyChapterInsightsData) {
      const surveyChapterInsights = surveyChapterInsightsData.filter(
        (insight) => insight.chapter_id === chapterId
      );
      setChapterInsights(surveyChapterInsights);
    }
  }, [surveyChapterInsightsData, chapterId]);

  useEffect(() => {
    if (surveyChapterScoringData) {
      const chapter = surveyChapterScoringData.chapters.find(
        (chapter) => chapter.id === chapterId
      );

      if (chapter) {
        setChapterScoring(chapter);
      } else {
        navigate("/404");
      }
    }
  }, [surveyChapterScoringData, chapterId, navigate]);

  useEffect(() => {
    if (surveyQuestionsScoring) {
      const chapterResponses = surveyQuestionsScoring.chapters.find(
        (chapter) => chapter.id === chapterId
      );

      if (chapterResponses?.questions) {
        setQuestionBreakdownForChapter(chapterResponses.questions);
      } else {
        navigate("/404");
      }
    }
  }, [surveyQuestionsScoring, chapterId, navigate]);

  useEffect(() => {
    if (
      surveyDispatchError?.response?.status === 404 ||
      surveyDispatchError?.response?.status === 400
    ) {
      navigate("/404");
    }
  }, [navigate, surveyDispatchError]);

  const handleSurveyScoringRefetch = (filterId: string) => {
    setSelectedDemographicFilter(filterId);
    surveyChapterScoringRefetch();
  };

  return (
    <>
      {surveyDispatchLoading && <LoadingIndicator />}
      {!surveyDispatchLoading && !surveyDispatchError && (
        <Grid container spacing={3} className="WorkplaceCultureChapterPage">
          <Grid item xs={12}>
            <WorkplaceCultureOverview
              title={chapterScoring?.name || ""}
              scoringData={chapterScoring?.demographic}
              isLoading={
                !!surveyDispatchError
                  ? surveyDispatchLoading
                  : surveyChapterScoringLoading
              }
              error={!!surveyDispatchError || !!surveyChapterScoringError}
              hasEnoughAnswers={surveyHasAnswers(chapterScoring?.demographic)}
              onScoringRefetch={handleSurveyScoringRefetch}
              selectedDemographicFilter={selectedDemographicFilter}
              isInChapter={true}
            />
          </Grid>
          <Grid item xs={12}>
            <InsightsCard
              insights={chapterInsights}
              isLoading={chapterInsightsLoading}
              error={!!chapterInsightsError}
              surveyClosed={isSurveyClosed(surveyDispatchData?.due_date)}
            />
          </Grid>
          <Grid item xs={12}>
            <QuestionBreakdownOverview
              questionBreakdownForChapter={questionBreakdownForChapter}
              isLoading={surveyQuestionsScoringLoading}
              error={!!surveyQuestionsScoringError}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};
